import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import { Breadcrumb } from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import { dramas, regions, years } from '@/mocks/dramas';

export interface CategoryConfig {
  slug: 'ethics' | 'latest' | 'classic';
  title: string;
  subtitle: string;
  description: string;
  intro: string;
  base: (typeof dramas)[number][];
}

const PAGE_SIZE = 12;
const scoreLevels = [
  { label: '全部评分', value: 'all' },
  { label: '9.0 分以上', value: '9' },
  { label: '8.5 分以上', value: '8.5' },
  { label: '8.0 分以上', value: '8' },
];
const sortOptions = [
  { label: '按发布时间', value: 'date' },
  { label: '按热度排序', value: 'popularity' },
  { label: '按评分排序', value: 'rating' },
];

export default function CategoryPage({ config }: { config: CategoryConfig }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') || '1') || 1;
  const year = searchParams.get('year') || 'all';
  const region = searchParams.get('region') || 'all';
  const score = searchParams.get('score') || 'all';
  const sort = searchParams.get('sort') || 'date';
  const [draftKeywords, setDraftKeywords] = useState('');
  const navigate = useNavigate();

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setSearchParams(next, { replace: false });
  };

  const filtered = useMemo(() => {
    const list = config.base.filter((d) => {
      if (year !== 'all' && d.year !== Number(year)) return false;
      if (region !== 'all' && d.region !== region) return false;
      if (score !== 'all' && d.rating < Number(score)) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (sort === 'popularity') return b.popularity - a.popularity;
      if (sort === 'rating') return b.rating - a.rating;
      return b.updateDate.localeCompare(a.updateDate);
    });
  }, [config.base, year, region, score, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) updateParam('page', String(totalPages));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  const resetFilters = () => setSearchParams(new URLSearchParams(), { replace: false });

  const goPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next);
  };

  const availableYears = years.filter((y) => config.base.some((d) => d.year === y));
  const availableRegions = regions.filter((r) => config.base.some((d) => d.region === r));
  const hasFilter = year !== 'all' || region !== 'all' || score !== 'all';

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1,
  );

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950">
        <div className="px-4 md:px-10 pt-6 pb-8 md:pt-8 md:pb-10">
          <div className="[&_a]:text-background-300 [&_a:hover]:text-background-50 [&_span]:text-background-200 [&_i]:text-background-500">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: config.title }]} />
          </div>
          <div className="mt-5 md:mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-accent-400 mb-2">
                <span className="w-4 h-px bg-accent-500"></span>
                {config.subtitle}
              </span>
              <h1 className="font-heading text-2xl md:text-[36px] leading-tight font-semibold text-background-50">
                {config.title}
              </h1>
              <p className="text-sm leading-7 text-background-300 mt-3">{config.intro}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div>
                <p className="font-heading text-2xl md:text-3xl text-accent-400">{filtered.length}</p>
                <p className="text-[11px] text-background-400 mt-1">部剧集</p>
              </div>
              <span className="w-px h-10 bg-background-800"></span>
              <div>
                <p className="font-heading text-2xl md:text-3xl text-background-50">{config.base.length}</p>
                <p className="text-[11px] text-background-400 mt-1">部资料收录</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-10 py-8 md:py-12">
        <div className="flex flex-col xl:flex-row xl:items-start gap-6 md:gap-8">
          <aside className="w-full xl:w-[260px] shrink-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 xl:sticky xl:top-24">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-600"></i>
                  筛选条件
                </h2>
                {hasFilter && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="text-[11px] text-primary-700 hover:text-primary-600 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    重置
                  </button>
                )}
              </div>

              <div className="space-y-5">
                <div>
                  <h3 className="text-xs font-medium text-foreground-700 mb-2.5">年份</h3>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[{ label: '全部', value: 'all' }, ...availableYears.map((y) => ({ label: `${y}年`, value: String(y) }))].map(
                      (item) => (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => updateParam('year', item.value)}
                          className={`h-8 rounded-md text-[11px] whitespace-nowrap transition-colors cursor-pointer ${
                            year === item.value
                              ? 'bg-primary-700 text-background-50'
                              : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                          }`}
                        >
                          {item.label}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-medium text-foreground-700 mb-2.5">地区</h3>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[{ label: '全部地区', value: 'all' }, ...availableRegions.map((r) => ({ label: r, value: r }))].map(
                      (item) => (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => updateParam('region', item.value)}
                          className={`h-8 rounded-md text-[11px] whitespace-nowrap transition-colors cursor-pointer ${
                            region === item.value
                              ? 'bg-primary-700 text-background-50'
                              : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                          }`}
                        >
                          {item.label}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-medium text-foreground-700 mb-2.5">
                    评分 <span className="text-foreground-400 font-normal">（演示数据）</span>
                  </h3>
                  <div className="flex flex-col gap-1.5">
                    {scoreLevels.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => updateParam('score', item.value)}
                        className={`h-8 px-3 rounded-md text-[11px] text-left whitespace-nowrap transition-colors cursor-pointer ${
                          score === item.value
                            ? 'bg-accent-100 text-accent-900'
                            : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-background-200">
                  <h3 className="text-xs font-medium text-foreground-700 mb-2.5">站内搜索</h3>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const q = draftKeywords.trim();
                      navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');                    }}
                    className="flex items-center h-9 rounded-md border border-background-300 bg-background-100 overflow-hidden focus-within:border-primary-400"
                  >
                    <input
                      value={draftKeywords}
                      onChange={(e) => setDraftKeywords(e.target.value)}
                      name="sidebar_q"
                      aria-label="站内搜索"
                      placeholder="剧名 / 演员 / 导演"
                      className="flex-1 h-full bg-transparent px-2.5 text-xs outline-none placeholder:text-foreground-400"
                    />
                    <button
                      type="submit"
                      aria-label="搜索"
                      className="w-9 h-9 flex items-center justify-center text-foreground-600 cursor-pointer"
                    >
                      <i className="ri-search-line text-sm"></i>
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-background-200">
              <p className="text-xs md:text-sm text-foreground-600">
                共找到 <span className="text-foreground-900 font-medium">{filtered.length}</span> 部剧集
                {hasFilter && <span className="text-foreground-400">（已应用筛选）</span>}
              </p>
              <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100 w-full sm:w-auto">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updateParam('sort', option.value)}
                    className={`flex-1 sm:flex-none h-8 px-3 md:px-3.5 rounded-full text-[11px] md:text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      sort === option.value
                        ? 'bg-background-50 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {pageItems.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
                <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-movie-2-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                  没有符合条件的剧集
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 mb-6 max-w-sm mx-auto leading-6">
                  当前筛选组合下暂无匹配作品，试着放宽年份或评分条件，也可以重置全部筛选重新浏览。
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2.5">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="h-10 px-5 rounded-md bg-primary-700 text-background-50 text-xs md:text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    重置筛选条件
                  </button>
                  <Link
                    to="/search"
                    className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-xs md:text-sm whitespace-nowrap flex items-center hover:border-primary-300 transition-colors"
                  >
                    改用关键词搜索
                  </Link>
                </div>
              </div>
            )}

            {filtered.length > PAGE_SIZE && (
              <nav aria-label="分页导航" className="mt-8 md:mt-10 flex items-center justify-center gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => goPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="上一页"
                  className="w-9 h-9 rounded-md border border-background-300 text-foreground-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {pageNumbers.map((n, idx) => (
                  <span key={n} className="flex items-center gap-1.5">
                    {idx > 0 && pageNumbers[idx - 1] !== n - 1 && (
                      <span className="text-foreground-400 text-xs px-1">…</span>
                    )}
                    <button
                      type="button"
                      onClick={() => goPage(n)}
                      aria-current={n === currentPage ? 'page' : undefined}
                      className={`w-9 h-9 rounded-md text-xs transition-colors cursor-pointer ${
                        n === currentPage
                          ? 'bg-primary-700 text-background-50'
                          : 'border border-background-300 text-foreground-700 hover:border-primary-300'
                      }`}
                    >
                      {n}
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => goPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="下一页"
                  className="w-9 h-9 rounded-md border border-background-300 text-foreground-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
                <span className="text-xs text-foreground-500 ml-2 whitespace-nowrap">
                  第 {currentPage} / {totalPages} 页
                </span>
              </nav>
            )}
          </div>
        </div>

        <div className="mt-12 md:mt-16 rounded-lg bg-background-100/70 border border-background-200 px-5 md:px-8 py-6 md:py-7">
          <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2.5">
            关于{config.title}
          </h2>
          <p className="text-xs md:text-sm leading-7 text-foreground-600 max-w-4xl">{config.description}</p>
        </div>
      </div>
    </SiteLayout>
  );
}